import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { HttpClient } from 'apiclient/ApiClient';
import { waitForReportData } from 'utils/reportUtils';

interface UnifiedReportResponse {
    report_id: string;
    message: string;
    error?: string;
}

interface ReportParams {
    type: 'class' | 'organization';
    id: string;
    startDate: string;
    endDate: string;
}

interface UseWeeklyReportCreationProps {
    onError?: (error: string) => void;
}

export const useWeeklyReportCreation = ({ onError }: UseWeeklyReportCreationProps = {}) => {
    const navigate = useNavigate();
    const httpClient = HttpClient();

    const mutation = useMutation<UnifiedReportResponse, Error, ReportParams>(
        async ({ type, id, startDate, endDate }) => {
            let url: string;
            
            if (type === 'class') {
                url = `/reports?class_uid=${id}&start=${startDate}&end=${endDate}`;
            } else {
                url = `/organization-reports?organization_id=${id}&start=${startDate}&end=${endDate}`;
            }
            
            const response = await httpClient.post(url, {});
            return (response as unknown) as UnifiedReportResponse;
        },
        {
            onSuccess: async (data, variables) => {
                if (data.error) {
                    onError?.(data.error);
                    return;
                }
                
                if (!data.report_id) {
                    if (data.message?.includes('No data found')) {
                        onError?.('No data found for the specified date range. Please try a different date range.');
                    } else {
                        onError?.('Unexpected response format');
                    }
                    return;
                }

                // Wait for report data to be available
                await waitForReportData(data.report_id, httpClient);
                
                // Navigate to appropriate report page
                const reportUrl = variables.type === 'class' 
                    ? `/teacher_stat/${data.report_id}`
                    : `/organization_stat/${data.report_id}`;
                
                navigate(reportUrl, { 
                    replace: true,
                    state: { 
                        fromWeeklyReport: true,
                        [variables.type === 'class' ? 'classId' : 'organizationId']: variables.id
                    }
                });
            },
            onError: (error) => {
                onError?.(error.message || 'Failed to create weekly report');
            }
        }
    );

    return {
        createReport: mutation.mutate,
        isLoading: mutation.isLoading,
        isError: mutation.isError,
        isSuccess: mutation.isSuccess,
        error: mutation.error
    };
};